// =============================================================================
// JobCard: the single card renderer for the Discover/Jobs tab's inbox +
// search results (brief (a): "render as cards bucketed home/bridge/dream/pass
// ... each card: title, company, location, score, bucket chip, 'as of'
// freshness when served, sponsorship read when present"). Reads straight off
// apps/throughline/src/lib/api.ts's JobRecord (the raw-SQL snake_case shape —
// see that file's JOBS module header) through jobBuckets.ts's pure formatters
// — no reshaping, no invented fields; every optional line renders ONLY when
// the server actually sent that signal (freshnessLabel/sponsorshipReadLabel
// are already null-safe).
//
// LANE tile-bucket (W20, his walk-fix, owner decision `tile-bucket` banked
// 2026-07-24 — docs/WALK-FINDINGS-2026-07-21.md's W20 row): the logo tile
// (first letter of the company) used to be colored by monoTone(company) — a
// company-NAME hash cycling exactly two DECORATIVE theme tokens (--tl-accent/
// --tl-warm), "purely decorative, no semantic meaning." But the SAME Jobs
// page teaches a legend where color = bucket (BucketColorLegend.tsx), so the
// decorative tint read as an unintended location/bucket claim — his walk:
// "why are some positions in Aus labeled as local? Others not?" (teal Avanade
// read as "local" purely from company-name arithmetic, while its real bucket
// was Unsorted). His pick: tiles JOIN the legend's language — the tile now
// carries the job's REAL bucket color, via the `.logo[data-bucket=...]`
// selectors below (JobCard.module.css) — the EXACT same colorVar family
// (theme/themes.ts's --tl-bucket-*) BucketColorLegend/BucketFilterChips/this
// file's own .bucketChip pill already resolve. One source of truth, never a
// second mapping: `bucketKey` below is the SAME `job.bucket ?? undetermined`
// resolution the .bucketChip pill already computes, reused verbatim for the
// tile. monoTone()/MONO_TONES are RETIRED (see the retirement note below) —
// no raw hex, no --tl-warm/--tl-accent decoratives on this tile anymore.
//
// The "open source" link (brief (c), capture hand-off): links job.canonical_url
// out with target="_blank" — the extension does capture, this tab only ever
// renders the truth already ingested. stopPropagation so tapping the link
// inside a card that is itself wrapped in a react-router Link (Jobs.tsx)
// opens the source in a new tab instead of also navigating to /jobs/:id.
//
// WAVE 1 / LANE C3 (QA #21): the location line now runs through
// orderLocationForUS (locationOrder.ts) — a pure US-first display reorder of
// a `;`-delimited multi-location string, same util JobDetail.tsx applies to
// its own header meta line. Never a re-score/re-bucket, single-location jobs
// pass through unchanged.
//
// AXIS-VIZ (R299 follow-up, owner's ruling: "it would be good to show a
// visual indicator of what's being derived — like the match score for a
// position"): a tiny "reads: Leadership · senior" chip, rendered ONLY when
// job.top_axis_key is served (a cheap LEFT JOIN listing_axis_reads by
// canonical_url on the list query, jobs.routes.ts — see JobRecord's own doc
// comment). NO READ, NO CHIP: this component never guesses, and never
// triggers a derivation from the list path (the one-call-per-listing law —
// a read is derived ONLY on the detail view's GET /jobs/:id, see
// AxisLensService.ts's header). Reuses AxisLensCard's own humanize/label
// helpers so a job's chip on the list and its full read on the detail page
// speak the identical display vocabulary.
//
// WAVE 2-A (QA #34): two additive badges, both SERVED, never re-derived here.
// (1) a "New" badge when job.new_since_last_visit is true (the server already
// decided this by comparing the job's created_at against the user's stored
// jobsLastSeenAt — see jobs.routes.ts's GET /jobs). (2) a tracking-status chip
// (trackingStatusLabel) when job.tracking_status carries a real applications
// row — this is the honest "applied / being tracked" marker Jason's
// walkthrough asked for; the actual SINK-TO-BOTTOM ordering itself is a
// server-side sort tier (TRACKING_RANK), not a client-side re-sort — this
// component only renders the badge, never moves the card.
//
// LANE W2.1-BUCKETS: the optional `onBucketChange` prop renders a
// BucketPicker beside the read-only bucket chip — the user-sort half of
// Jason's ruling ("pass is user selected and then unsorted to be sorted by
// user"). Omitted entirely when the caller doesn't pass a handler (e.g. a
// future read-only context), so this stays additive and never forces every
// JobCard consumer to wire a write path. The actual PATCH /jobs/:id/bucket
// call lives in useJobsInbox's setJobBucket — JobCard only renders + reports
// the user's pick (no-frontend-bandaids: the write is a data-layer concern).
// =============================================================================

import { ScoreRing } from './ScoreRing';
import { BucketPicker } from './BucketPicker';
import styles from './JobCard.module.css';
import type { JobRecord } from '../lib/api';
import {
  type JobBucket,
  UNDETERMINED_BUCKET_KEY,
  axisGlanceLabel,
  bucketChipLabel,
  displayLocationForCard,
  freshnessLabel,
  jobFitScore,
  scoreConfidenceChipLabel,
  sponsorshipReadLabel,
  staleListingNotice,
  trackingStatusLabel,
} from '../lib/jobBuckets';
import { orderLocationForUS } from '../lib/locationOrder';

export interface JobCardProps {
  job: JobRecord;
  /** LANE W2.1-BUCKETS — when supplied, renders a BucketPicker so the user
   *  can explicitly set this job's bucket. Omitted ⇒ the card stays
   *  read-only (just the chip), no picker rendered. Return value is
   *  deliberately untyped/ignored — callers like useJobsInbox's
   *  setJobBucket resolve to a result object BucketPicker doesn't need. */
  // biome-ignore lint/suspicious/noExplicitAny: caller's resolved value is intentionally unused here
  onBucketChange?: (jobId: string, bucket: JobBucket) => Promise<any> | void;
  /** Rendered as a Link by the caller (Jobs.tsx uses react-router's Link,
   *  JobDetail-adjacent contexts may not need navigation) — JobCard itself
   *  stays navigation-agnostic and just renders the card body; the caller
   *  wraps it. */
  children?: never;
}

// RETIRED (LANE tile-bucket, W20): monoTone(company) + its MONO_TONES
// (--tl-accent / --tl-warm cycle) used to color this tile. Removed outright,
// not merely unused — the whole point of this fix is that the tile's color
// is no longer an arbitrary, decorative, company-name-derived value; it is
// the job's real bucket (see the header comment above + the
// `.logo[data-bucket=...]` rules in JobCard.module.css). If a future need
// resurfaces for a purely-decorative per-item tint, it should NOT reuse this
// name or these tokens — --tl-accent/--tl-warm are exactly the two hues this
// fix proved collide with the bucket legend.

export function JobCard({ job, onBucketChange }: JobCardProps) {
  const fit = jobFitScore(job);
  const bucketLabel = bucketChipLabel(job.bucket);
  // LANE tile-bucket (W20) — the ONE resolution of "which bucket key does
  // this job display as," reused verbatim for both the logo tile's
  // data-bucket and the bucketChip pill's data-bucket a few lines down
  // (previously only the pill computed this inline) — one source of truth,
  // never a second mapping between the two.
  const bucketKey = job.bucket ?? UNDETERMINED_BUCKET_KEY;
  const freshness = freshnessLabel(job);
  // W21 — the honest "may no longer be advertised" notice; null unless the
  // server's own isStale verdict is true (see jobBuckets.ts's own doc
  // comment). Independent of `freshness` above: freshness always renders the
  // routine "as of Xd ago" fact when known, this ADDS the honesty flag once
  // that fact crosses the staleness threshold — the two can render together.
  const staleNotice = staleListingNotice(job);
  const sponsorship = sponsorshipReadLabel(job);
  const initial = job.company.trim().charAt(0).toUpperCase() || '?';
  // QA #21 — pure display reorder of a `;`-delimited multi-location string,
  // US-relevant segment first; a single-location job.location is unchanged.
  const orderedLocation = orderLocationForUS(job.location);
  // BOARD-TRUTH FIX (owner report: a "Nearby" card showed a foreign city as
  // its face) — prefers the SERVED resolved_location (the segment that
  // actually justified home/relocate) over the raw ordered string for those
  // two buckets only; every other bucket renders orderedLocation exactly as
  // before this fix.
  const displayLocation = displayLocationForCard(job, orderedLocation);
  // QA #34 — both server-served, both undefined-safe (POST /jobs/search rows
  // don't carry either field yet — see JobRecord's own doc comment — so a
  // search result simply renders neither badge, never a fabricated "not new").
  const isNew = job.new_since_last_visit === true;
  const trackingLabel = trackingStatusLabel(job.tracking_status);
  // AXIS-VIZ — jobBuckets.ts's axisGlanceLabel already carries the
  // no-invention, both-fields-or-neither contract; see that function's own
  // doc comment.
  const axisGlance = axisGlanceLabel(job);
  // CLASS 10 (D21) — the honest "what I know so far" chip, replacing a bare
  // undetermined dash. null when the job IS scored (ScoreRing renders) or
  // the server didn't serve score_confidence at all (search results this
  // pass).
  const confidenceChip = scoreConfidenceChipLabel(job);

  return (
    <div className={styles.body}>
      <div className={styles.logo} data-bucket={bucketKey}>
        {initial}
      </div>
      <div className={styles.info}>
        <div className={styles.titleRow}>
          <div className={styles.title}>{job.title}</div>
          {isNew ? <span className={styles.newBadge}>New</span> : null}
          <span className={styles.bucketChip} data-bucket={bucketKey}>
            {bucketLabel}
          </span>
          {onBucketChange ? (
            <BucketPicker bucket={job.bucket} onChange={(bucket) => onBucketChange(job.id, bucket)} />
          ) : null}
        </div>
        <div className={styles.meta}>
          {job.company}
          {displayLocation ? ` · ${displayLocation}` : ''}
        </div>
        <div className={styles.detailRow}>
          {trackingLabel ? <span className={styles.trackingChip}>{trackingLabel}</span> : null}
          {sponsorship ? <span className={styles.detailChip}>{sponsorship}</span> : null}
          {/* AXIS-VIZ — the glance chip. ONE ramp only (var(--tl-accent)/
              var(--tl-accent-soft), the exact pairing AxisLensSignature's own
              register chip uses on the detail page) so a job's "reads as"
              read speaks the same visual language everywhere it appears —
              never a bucket hue, never --tl-warm (that tone stays reserved
              for tracking/sponsorship's own "honest extra fact" chips right
              beside this one). */}
          {axisGlance ? <span className={styles.axisChip}>{axisGlance}</span> : null}
          {freshness ? <span className={styles.freshness}>{freshness}</span> : null}
          <a
            href={job.canonical_url}
            target="_blank"
            rel="noreferrer"
            className={styles.sourceLink}
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Open source
          </a>
          {/* W21 — a plain warning beside the outbound link, never a blocked
              link: he can still choose to open it, he just isn't told a
              stranger's stale posting is confidently live. */}
          {staleNotice ? <span className={styles.staleNotice}>{staleNotice}</span> : null}
        </div>
      </div>
      {fit !== null ? (
        <ScoreRing pct={fit} size={46} fontSize={15} />
      ) : confidenceChip ? (
        // CLASS 10 (D21) — "Instead of saying 'I don't know' it should say
        // 'I DO know — this is what I know SO FAR'." NO number ring here —
        // an honest chip naming the known facets instead of a fabricated 0
        // or a bare dash.
        <div className={styles.confidenceChip} title={confidenceChip}>
          {confidenceChip}
        </div>
      ) : (
        <div className={styles.unscored} title="Not yet scored">
          —
        </div>
      )}
      {/* WIRE-AUDIT fix (I3's affordance gap): the card has ALWAYS been a
          Link to /jobs/:id (Jobs.tsx wraps it) — this chevron just makes
          that visible so the detail page is discoverable. currentColor +
          opacity only; no new tokens. */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        style={{ flexShrink: 0, alignSelf: 'center', opacity: 0.45 }}
      >
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
